import { useEffect, useRef, useState } from 'react';
import type { Notice } from '../../api/notices';
import NoticeBanner, { IMPERSONATION_DISMISS_REASON } from './NoticeBanner';
import { HELP_LINKS } from './helpLinks';

interface Props {
  notices: Notice[];
  canDismiss: boolean;
  onDismiss: (id: string) => void;
}

const TITLE_ID = 'notice-bell-title';

export default function NoticeBell({ notices, canDismiss, onDismiss }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const unread = notices.filter((n) => !n.dismissed).length;
  const ordered = [...notices.filter((n) => !n.dismissed), ...notices.filter((n) => n.dismissed)];

  // Escape and outside-click listeners are only attached while the panel is open, and Escape
  // returns focus to the bell button that opened it.
  useEffect(() => {
    if (!open) return;
    titleRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    function handleMouseDown(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Notices (${unread} unread)` : 'Notices'}
        aria-expanded={open}
        className="relative rounded-md p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 00-4-5.7V5a2 2 0 10-4 0v.3C7.7 6.2 6 8.4 6 11v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-labelledby={TITLE_ID}
          className="fixed inset-x-4 top-16 z-50 max-h-[70vh] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 shadow-lg sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[28rem]"
        >
          <h2 id={TITLE_ID} ref={titleRef} tabIndex={-1} className="mb-3 text-sm font-semibold text-gray-900 outline-none">
            Notices
          </h2>
          {ordered.length === 0 ? (
            <p className="text-sm text-gray-600">No notices</p>
          ) : (
            <div className="space-y-3">
              {ordered.map((n) => (
                <NoticeBanner
                  key={n.id}
                  notice={n}
                  canDismiss={canDismiss && !n.dismissed}
                  onDismiss={onDismiss}
                  muted={n.dismissed}
                  headingLevel={3}
                  collapsible
                  dismissDisabledReason={!n.dismissed && !canDismiss ? IMPERSONATION_DISMISS_REASON : undefined}
                />
              ))}
            </div>
          )}
          <a href={HELP_LINKS.notices} target="_blank" rel="noopener noreferrer" className="mt-4 block text-sm text-blue-700 hover:underline">
            About notices
          </a>
        </div>
      )}
    </div>
  );
}
